import { useParams } from "react-router-dom";
import useUser from "@/features/users/hooks/use-user";
import ThreeDotsLoader from "@/components/common/three-dots-loader";
import { Card, CardContent } from "@/components/ui/card";
import { HasAccess } from "@/components/common/has-access";
import VaulDrawer from "@/components/ui/drawer";
import { useState } from "react";
import { UpdateUserDetailsDialog } from "@/features/users/ui/update-user-details-dialog";
import AddEditBundleUsageForm from "@/features/users/ui/usage-form";
import { UserProfileHeader } from "@/features/users/ui/user-profile-header";
import { CopyReschedulePaymentLinkButton } from "@/features/users/ui/copy-reschedule-payment-link-button";
import { UserInfoGrid } from "@/features/users/ui/user-info-grid";
import { UserSummaryCard } from "@/features/users/ui/user-summary-card";
import { BundleUsageSection } from "@/features/users/ui/bundle-usage-section";
import {
  calculateSessionsSummary,
  calculatePaymentSummary,
  groupBundlesByName,
  findClosestToExpiry,
} from "@/features/users/utils/user-calculations";
import { useUsageException } from "@/features/users/hooks/use-usage-exception";
import { useFreeSession } from "@/features/users/hooks/use-free-session";

export default function UserDetails() {
  const [showBundleUsageFormDrawer, setShowBundleUsageFormDrawer] = useState(false);
  const [showUpdateUserDetailsDialog, setShowUpdateUserDetailsDialog] = useState(false);
  const { id } = useParams<{ id: string }>();
  const { data: user, isLoading, error, isFetching } = useUser(id!);
  const { error: usageExceptionError } = useUsageException();
  const { error: addFreeSessionError } = useFreeSession();

  if (error || usageExceptionError || addFreeSessionError) {
    return <></>;
  }

  if (isFetching || isLoading || !user) {
    return (
      <div className="w-full flex justify-center items-center min-h-[400px]">
        <ThreeDotsLoader />
      </div>
    );
  }

  // Calculate summaries
  const sessionsSummary = calculateSessionsSummary(user.bundles_usage);
  const sessionsRemaining = sessionsSummary.total - sessionsSummary.used;
  const paymentSummary = calculatePaymentSummary(user.bundles_usage);
  const bundlesGroupedByName = groupBundlesByName(user.bundles_usage);
  const closestToExpiry = findClosestToExpiry(user.bundles_usage);
  const exceptionLogs = user.exceptionLogs || [];

  return (
    <div className="w-full py-4 space-y-4 px-6">
      <Card className="bg-sidebar-primary text-white">
        <CardContent className="p-4">
          <div className="grid grid-cols-10 gap-4">
            {/* Left side - 70% */}
            <div className="col-span-7 space-y-3">
              <UserProfileHeader
                fullName={user.full_name}
                onEditClick={() => setShowUpdateUserDetailsDialog(true)}
              />
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <CopyReschedulePaymentLinkButton
                  user={user}
                  line="therapy"
                  className="bg-transparent text-white border-white hover:bg-white/10 hover:text-white"
                />
                <CopyReschedulePaymentLinkButton
                  user={user}
                  line="clinical"
                  className="bg-transparent text-white border-white hover:bg-white/10 hover:text-white"
                />
                <CopyReschedulePaymentLinkButton
                  user={user}
                  line="psychiatry"
                  className="bg-transparent text-white border-white hover:bg-white/10 hover:text-white"
                />
              </div>
              <UserInfoGrid user={user} />
            </div>

            {/* Right side - 30% - Summary Section */}
            <div className="col-span-3">
              <UserSummaryCard
                sessionsSummary={sessionsSummary}
                sessionsRemaining={sessionsRemaining}
                paymentSummary={paymentSummary}
                bundlesGroupedByName={bundlesGroupedByName}
                closestToExpiry={closestToExpiry}
                exceptionLogs={exceptionLogs}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <BundleUsageSection
        user={user}
        onCreatePackClick={() => setShowBundleUsageFormDrawer(true)}
      />

      <HasAccess permission="write">
        <VaulDrawer
          drawerDirection="right"
          isOpen={showBundleUsageFormDrawer}
          onOpenChange={setShowBundleUsageFormDrawer}
          triggerChildren={null}
          contentChildren={
            <AddEditBundleUsageForm
              editMode={false}
              onClose={() => setShowBundleUsageFormDrawer(false)}
              initialData={null}
              userId={user.id}
            />
          }
        />
        <VaulDrawer
          drawerDirection="right"
          isOpen={showUpdateUserDetailsDialog}
          onOpenChange={setShowUpdateUserDetailsDialog}
          triggerChildren={null}
          contentChildren={
            <UpdateUserDetailsDialog
              user={user}
              onClose={() => setShowUpdateUserDetailsDialog(false)}
            />
          }
        />
      </HasAccess>
    </div>
  );
}
